import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, formatUsd } from '../lib/api';
import { Button, Card, Field, Input, Select, Alert, Modal } from '../components/ui';
import {
  CodecFields, PictureFields, RtmpEditor, CostBreakdown, newRtmp, ClipUploader, OutputListEditor, DEFAULT_OUTPUT,
  type EncoderOptions, type Encode, type Rtmp, type CostEstimate, type Clip, type FlowOutputSpec,
} from '../components/encoder-forms';

/** A saved encoder setup - see templateSpec on the server for what it keeps. */
export interface EncoderTemplate {
  id: string;
  name: string;
  description: string;
  spec: {
    name: string; dualIngest: boolean; ingestPorts: number[]; audioPairs: number; encodes: Encode[];
    dashboardPreview: boolean; rtmp: Rtmp[]; overlayEnabled: boolean; ingestOutputs?: FlowOutputSpec[][];
  };
  createdBy: string | null;
  createdAt: string;
}

/** Mirrors plannedNames() on the server, so the names show as you type. */
function plannedNames(name: string, dualIngest: boolean, encodes: Encode[], rtmp: Rtmp[], onFlow = false) {
  const n = name.trim().toUpperCase() || 'NAME';
  // Added to an existing flow, it makes no ingest flows of its own.
  const ingest = onFlow ? [] : dualIngest ? [`${n}-INGEST-MAIN`, `${n}-INGEST-BACKUP`] : [`${n}-INGEST-MAIN`];
  const outputs = encodes.flatMap((e, i) => {
    const label = i === 0 ? 'MAIN' : (e.label.trim().toUpperCase() || `ENCODE${i + 1}`);
    const base = i === 0 ? n : `${n}-${label}`;
    const main = i === 0 ? `${n}-MAIN` : base;
    return e.dualFlows ? [main, `${base}-BACKUP`] : [main];
  });
  const rtmpChannels = rtmp.map((r) => `${n}-RTMP-${r.label || '…'}`);
  return { channel: `${n}-ENCODER`, ingest, outputs, rtmpChannels };
}

function Toggle({ value, onChange, options }: {
  value: boolean;
  onChange: (v: boolean) => void;
  options: [string, string];
}) {
  return (
    <div className="inline-flex rounded-md ring-1 ring-hairline">
      {[false, true].map((v, i) => (
        <button
          key={String(v)}
          type="button"
          onClick={() => onChange(v)}
          className={`px-3 py-1.5 text-sm ${i === 0 ? 'rounded-l-md' : 'rounded-r-md'} ${
            value === v ? 'bg-series-1 text-white' : 'text-ink-secondary hover:bg-raised'
          }`}
        >
          {options[i]}
        </button>
      ))}
    </div>
  );
}

export function LaunchEncoder() {
  const navigate = useNavigate();
  const [opts, setOpts] = useState<EncoderOptions | null>(null);
  const [name, setName] = useState('');
  const [dualIngest, setDualIngest] = useState(true);
  const [ingestPorts, setIngestPorts] = useState<number[]>([5000, 5000]);
  const [audioPairs, setAudioPairs] = useState(2);
  const [encodes, setEncodes] = useState<Encode[]>([]);
  const [dashboardPreview, setDashboardPreview] = useState(true);
  const [overlayEnabled, setOverlayEnabled] = useState(false);
  const [overlayUrl, setOverlayUrl] = useState('');
  const [rtmp, setRtmp] = useState<Rtmp[]>([]);
  const [media, setMedia] = useState<Clip[]>([]);
  const [uploading, setUploading] = useState(false);
  const [estimate, setEstimate] = useState<CostEstimate | null>(null);
  const [estimateNote, setEstimateNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ingestOutputs, setIngestOutputs] = useState<FlowOutputSpec[][]>([[], []]);
  const [templates, setTemplates] = useState<EncoderTemplate[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [tplEdit, setTplEdit] = useState<{ name: string; description: string } | null>(null);
  const [tplBusy, setTplBusy] = useState(false);
  // Record it too: as soon as it is running, or from a time. Sent as `recording`
  // with the launch; the hub starts the recorder once the encoder is up.
  const [rec, setRec] = useState({
    on: false, when: 'asap' as 'asap' | 'at', at: '', hours: '',
    resolution: '', fps: '', mbps: 8, pairs: [1] as number[], kbps: 192, transcribe: false,
  });
  const [params] = useSearchParams();
  // ?flow=<id>: the encoder is being added to a flow that already exists. That
  // flow is its ingest; it is not stopped, and stays run from its own page.
  const flowId = params.get('flow');
  const [flow, setFlow] = useState<{ id: string; name: string; sourceAddress: string | null } | null>(null);
  useEffect(() => {
    if (!flowId) return;
    api.get<{ flow: { id: string; name: string; sourceAddress: string | null } }>(`/flows/${flowId}`).then((r) => {
      setFlow(r.flow);
      setName((n) => n || r.flow.name.slice(0, 40));
      setDualIngest(false);
    }).catch((err: Error) => setError(err.message));
  }, [flowId]);

  const loadTemplates = () =>
    api.get<{ templates: EncoderTemplate[] }>('/encoders/templates').then((r) => { setTemplates(r.templates); return r.templates; });

  useEffect(() => {
    Promise.all([api.get<EncoderOptions>('/encoders/options'), loadTemplates().catch(() => [] as EncoderTemplate[])]).then(([o, ts]) => {
      setOpts(o);
      setEncodes([o.defaultEncode]);
      // From "Launch one like this" after saving a template.
      const t = ts.find((x) => x.id === params.get('template'));
      if (t) applyTemplate(t);
    }).catch((err: Error) => setError(err.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Fill the form in from a template. Clips, stream keys and receivers' addresses are the operator's to add. */
  function applyTemplate(t: EncoderTemplate) {
    const s = t.spec;
    setTemplateId(t.id);
    setName(s.name);
    setDualIngest(s.dualIngest);
    setIngestPorts([s.ingestPorts[0] ?? 5000, s.ingestPorts[1] ?? s.ingestPorts[0] ?? 5000]);
    setAudioPairs(s.audioPairs);
    setEncodes(s.encodes.map((e) => ({ ...e })));
    setIngestOutputs([s.ingestOutputs?.[0] ?? [], s.ingestOutputs?.[1] ?? []]);
    setDashboardPreview(s.dashboardPreview);
    setOverlayEnabled(s.overlayEnabled);
    setOverlayUrl('');
    setRtmp(s.rtmp.map((r) => ({ ...r, destinations: r.destinations.map((d) => ({ ...d })) })));
    setMedia([]);
  }

  const template = templates.find((t) => t.id === templateId) ?? null;

  const spec = useMemo(() => ({
    name, dualIngest, audioPairs, encodes, dashboardPreview,
    ingestPorts: dualIngest ? ingestPorts : ingestPorts.slice(0, 1),
    ingestOutputs: dualIngest ? ingestOutputs : ingestOutputs.slice(0, 1),
    rtmp, overlayEnabled, overlayUrl, media,
  }), [name, dualIngest, audioPairs, encodes, dashboardPreview, ingestPorts, ingestOutputs, rtmp, overlayEnabled, overlayUrl, media]);

  // What the recording asks for, in the shape the server takes; null when off.
  const recordingBody = useMemo(() => (rec.on ? {
    spec: {
      resolution: rec.resolution || (encodes[0]?.resolution === '1080i' ? '1080p' : encodes[0]?.resolution), fps: rec.fps || encodes[0]?.fps, mbps: rec.mbps,
      audioPairs: rec.pairs.filter((p) => p <= audioPairs), audioKbps: rec.kbps, transcribe: rec.transcribe,
    },
    startAt: rec.when === 'at' && rec.at ? new Date(rec.at).toISOString() : null,
    stopAfterHours: rec.hours ? Number(rec.hours) : null,
  } : undefined), [rec, encodes, audioPairs]);

  // The hourly cost, re-priced as the form changes - the recording included.
  // Debounced: the price list is cached on the server, but there is no point
  // asking on every keystroke.
  useEffect(() => {
    if (!encodes.length) return;
    const t = setTimeout(() => {
      api.post<CostEstimate>('/encoders/estimate', { ...spec, name: spec.name || 'PREVIEW', recording: recordingBody })
        .then((e) => { setEstimate(e); setEstimateNote(null); })
        .catch((err: Error) => setEstimateNote(err.message));
    }, 400);
    return () => clearTimeout(t);
  }, [spec, encodes.length, recordingBody]);

  const names = useMemo(() => plannedNames(name, dualIngest, encodes, rtmp, !!flowId), [name, dualIngest, encodes, rtmp, flowId]);

  const update = (i: number, patch: Partial<Encode>) =>
    setEncodes((es) => es.map((e, j) => (j === i ? { ...e, ...patch } : e)));

  // An encode's outputs, a leg at a time; untouched, they are the one RECEIVER.
  const legOutputs = (e: Encode, leg: 'main' | 'backup') => (e.outputs ? e.outputs[leg] ?? [] : [DEFAULT_OUTPUT]);
  const setLeg = (i: number, leg: 'main' | 'backup', v: FlowOutputSpec[]) => {
    const e = encodes[i];
    update(i, { outputs: { main: legOutputs(e, 'main'), backup: legOutputs(e, 'backup'), [leg]: v } });
  };

  async function tplAction(fn: () => Promise<unknown>) {
    setTplBusy(true);
    try {
      await fn();
      await loadTemplates();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setTplBusy(false);
    }
  }

  const recordingAtInPast = rec.on && rec.when === 'at' && !!rec.at && new Date(rec.at).getTime() < Date.now() - 60_000;

  async function launch() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ id: string; recording?: { ok: boolean; message?: string } | null }>(
        flowId ? `/flows/${flowId}/encoder` : '/encoders', { ...spec, recording: recordingBody },
      );
      if (r.recording && !r.recording.ok) window.alert(`The encoder is launching, but the recording could not be set up: ${r.recording.message}`);
      navigate(`/encoders/${r.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Launch failed.');
    } finally {
      setBusy(false);
    }
  }

  if (!opts) return error ? <Alert tone="critical">{error}</Alert> : null;

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
      <div className="space-y-4">
        {error && <Alert tone="critical">{error}</Alert>}

        <Card
          title="Template"
          action={template && (
            <div className="flex gap-1">
              <Button variant="ghost" onClick={() => setTplEdit({ name: template.name, description: template.description })}>Rename</Button>
              <Button variant="ghost" loading={tplBusy} onClick={() => {
                if (window.confirm(`Delete the template "${template.name}"? Encoders launched from it are not affected.`)) {
                  void tplAction(async () => { await api.delete(`/encoders/templates/${template.id}`); setTemplateId(''); });
                }
              }}>Delete</Button>
            </div>
          )}
        >
          <div className="space-y-2 p-4 text-sm">
            {templates.length === 0 ? (
              <p className="text-xs text-ink-muted">
                None saved yet. Open an encoder and press Save as template to launch others like it from here.
              </p>
            ) : (
              <>
                <Select value={templateId} onChange={(e) => {
                  const t = templates.find((x) => x.id === e.target.value);
                  if (t) applyTemplate(t); else setTemplateId('');
                }}>
                  <option value="">Start from scratch</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Select>
                {template && (
                  <>
                    {template.description && <p className="whitespace-pre-line text-ink-secondary">{template.description}</p>}
                    <p className="text-xs text-ink-muted">
                      Filled in below. Change the name, then put in each receiver's address and every RTMP server
                      and stream key - the template has 0.0.0.0 and blanks where they were. Clips are not kept.
                      {template.createdBy && ` Saved by ${template.createdBy}.`}
                    </p>
                  </>
                )}
              </>
            )}
          </div>
        </Card>

        <Card title="Encoder">
          <div className="grid gap-4 p-4 sm:grid-cols-2">
            <Field label="Name" hint="Prefixes every resource it makes, e.g. SIB1XV.">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
                placeholder="SIB1XV"
                maxLength={40}
                autoFocus
              />
            </Field>
            <Field label="Audio pairs" hint="Pair N is TS audio track N, each stereo - as SIB1XV maps it.">
              <Select value={audioPairs} onChange={(e) => setAudioPairs(Number(e.target.value))}>
                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} pair{n === 1 ? '' : 's'}</option>)}
              </Select>
            </Field>
            {flowId && (
              <div className="space-y-1 sm:col-span-2">
                <div className="text-sm font-medium">Ingest</div>
                <p className="text-sm text-ink-secondary">
                  The flow <span className="font-mono">{flow?.name ?? '…'}</span>, as it is. It keeps running while the encoder is
                  built and is not changed: its source and outputs are still run from its own page, and stopping or deleting
                  the encoder leaves it alone.
                </p>
              </div>
            )}
            <div className={`space-y-2 sm:col-span-2 ${flowId ? 'hidden' : ''}`}>
              <div className="text-sm font-medium">Ingest</div>
              <Toggle value={dualIngest} onChange={setDualIngest} options={['Single SRT ingest', 'Dual ingest with failover']} />
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={dualIngest ? 'Main ingest port' : 'Ingest port'}>
                  <Input type="number" min={1024} max={65535} value={ingestPorts[0]}
                    onChange={(e) => setIngestPorts([Number(e.target.value), ingestPorts[1]])} />
                </Field>
                {dualIngest && (
                  <Field label="Backup ingest port">
                    <Input type="number" min={1024} max={65535} value={ingestPorts[1]}
                      onChange={(e) => setIngestPorts([ingestPorts[0], Number(e.target.value)])} />
                  </Field>
                )}
              </div>
              <p className="text-xs text-ink-muted">
                {dualIngest
                  ? 'Two SRT listener flows - main and backup links from the venue, each on its own address. MediaLive switches to the backup after a second of input loss, and back once the main has been clean for three.'
                  : 'One SRT listener flow. If it drops, the outputs carry slate until it returns.'}
                {' '}Ports can be changed afterwards.
              </p>
            </div>
          </div>
        </Card>

        <Card
          title="Encodes"
          action={
            <Button
              variant="ghost"
              onClick={() => setEncodes((es) => [...es, { ...opts.defaultEncode, label: '', resolution: '720p', mbps: 5, dualFlows: false }])}
              disabled={encodes.length >= 8}
            >
              + Add encode
            </Button>
          }
        >
          <div className="divide-y divide-grid">
            {encodes.map((e, i) => (
              <div key={i} className="space-y-3 p-4">
                <div className="grid items-end gap-3 sm:grid-cols-5">
                  <Field label={i === 0 ? 'Encode 1' : `Encode ${i + 1} name`}>
                    {i === 0 ? (
                      <div className="py-1.5 text-sm text-ink-secondary">MAIN</div>
                    ) : (
                      <Input
                        value={e.label}
                        onChange={(ev) => update(i, { label: ev.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '') })}
                        placeholder="LOWRES"
                        maxLength={20}
                      />
                    )}
                  </Field>
                  <PictureFields opts={opts} value={e} onChange={(p) => update(i, p)} resolutions={opts.encodeResolutions} />
                  <Field label="Output flows">
                    <Select value={e.dualFlows ? 'dual' : 'single'} onChange={(ev) => update(i, { dualFlows: ev.target.value === 'dual' })}>
                      <option value="single">1 flow</option>
                      <option value="dual">2 flows (redundant)</option>
                    </Select>
                  </Field>
                </div>
                <div className="grid items-end gap-3 sm:grid-cols-5">
                  <CodecFields opts={opts} value={e} onChange={(p) => update(i, p)} />
                  <div className="pb-1">
                    {i > 0 && (
                      <Button variant="ghost" onClick={() => setEncodes((es) => es.filter((_, j) => j !== i))}>Remove encode</Button>
                    )}
                  </div>
                </div>
                {estimate && (
                  <div className="text-xs text-ink-muted">
                    {formatUsd(estimate.parts.find((p) => p.name.startsWith(`${i === 0 ? 'MAIN' : (e.label || `ENCODE${i + 1}`)} encode`))?.usdPerHour ?? 0, 3)}/hr for this encode
                    {e.codec === 'H265' && ' - HEVC costs about three times H.264 in MediaLive'}
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="px-4 pb-3 text-xs text-ink-muted">
            CBR, two second GOP; everything else MediaLive's defaults. Each output flow gets an SRT listener
            for the receiver - more can be added, and any can be switched off, once it is running.
          </p>
        </Card>

        <Card title="Receiver outputs">
          <div className="space-y-4 p-4">
            <p className="text-xs text-ink-muted">
              The SRT outputs each flow is made with - listeners a receiver pulls from, or callers that push to
              them. Any can be changed, added or switched off once it is running.
            </p>
            {names.ingest.map((flowName, i) => (
              <div key={flowName} className="space-y-1">
                <div className="font-mono text-xs">{flowName}</div>
                <OutputListEditor value={ingestOutputs[i] ?? []}
                  onChange={(v) => setIngestOutputs((io) => io.map((x, j) => (j === i ? v : x)))} />
              </div>
            ))}
            {encodes.map((e, i) => {
              const first = encodes.slice(0, i).reduce((n, x) => n + (x.dualFlows ? 2 : 1), 0);
              return (e.dualFlows ? (['main', 'backup'] as const) : (['main'] as const)).map((leg, k) => (
                <div key={`${i}-${leg}`} className="space-y-1">
                  <div className="font-mono text-xs">{names.outputs[first + k]}</div>
                  <OutputListEditor value={legOutputs(e, leg)} onChange={(v) => setLeg(i, leg, v)} />
                </div>
              ));
            })}
          </div>
        </Card>

        <Card
          title="RTMP encodes"
          action={<Button variant="ghost" onClick={() => setRtmp((rs) => [...rs, newRtmp()])} disabled={rtmp.length >= 8}>+ Add RTMP</Button>}
        >
          {rtmp.length === 0 ? (
            <p className="p-4 text-sm text-ink-muted">
              None. RTMP (YouTube, Facebook, a CDN) runs on its own small channel fed from the ingest flows,
              so it can be started, stopped and re-keyed without touching the main encoder. Add one here or
              later from the encoder page.
            </p>
          ) : (
            <div className="divide-y divide-grid">
              {rtmp.map((r, i) => (
                <div key={i} className="space-y-2 p-4">
                  <RtmpEditor opts={opts} value={r} audioPairs={audioPairs}
                    onChange={(v) => setRtmp((rs) => rs.map((x, j) => (j === i ? v : x)))} />
                  <Button variant="ghost" onClick={() => setRtmp((rs) => rs.filter((_, j) => j !== i))}>Remove RTMP encode</Button>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Preview and overlay">
          <div className="space-y-4 p-4 text-sm">
            <label className="flex items-start gap-3">
              <input type="checkbox" className="mt-1" checked={dashboardPreview} onChange={(e) => setDashboardPreview(e.target.checked)} />
              <span>
                Dashboard preview
                <span className="block text-xs text-ink-muted">
                  An extra 540p encode played live on the encoder's page, under a second behind the channel.
                  Flow thumbnails are there either way.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3">
              <input type="checkbox" className="mt-1" checked={overlayEnabled} onChange={(e) => setOverlayEnabled(e.target.checked)} />
              <span>
                HTML overlay
                <span className="block text-xs text-ink-muted">
                  Burns a web page - a score bug, a lower third - into every encode of the main channel. Put on
                  air, taken off and switched to another page from the encoder page, immediately, without a
                  restart. MediaLive bills for it while the channel runs, on air or not.
                </span>
              </span>
            </label>
            {overlayEnabled && (
              <Field label="Overlay page" hint="Can be changed later. Nothing is shown until you put it on air.">
                <Input value={overlayUrl} onChange={(e) => setOverlayUrl(e.target.value.trim())} placeholder="https://graphics.example.com/scorebug" />
              </Field>
            )}
          </div>
        </Card>

        <Card title="Media clips">
          <div className="space-y-3 p-4 text-sm">
            <p className="text-xs text-ink-muted">
              Stings, ad breaks, holding loops - cut to one from the encoder page and the channel plays it, then
              cuts back to the live feed by itself when it ends. MP4 with H.264 or HEVC video and AAC audio, up to
              5 GB each; the clip's first audio track plays on every pair. Adding or removing clips later restarts
              the channel, so upload them now if you can.
            </p>
            <ClipUploader value={media} onChange={setMedia} onBusy={setUploading} />
          </div>
        </Card>

        <Card
          title="Recording"
          action={
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" className="accent-series-1" checked={rec.on} onChange={(e) => setRec({ ...rec, on: e.target.checked })} />
              Record this encoder
            </label>
          }
        >
          <div className="space-y-3 p-4 text-sm">
            <p className="text-xs text-ink-muted">
              Records what arrives at the ingest to S3 on its own small channel, so it can be played, trimmed, downloaded and
              shared from the Recordings tab. Start it as soon as the encoder is running, or set a time: the hub starts the
              recorder then (and waits up to two hours for the encoder to be running). The recording stops by itself after the
              hours you give, or when the encoder stops.
            </p>
            {rec.on && (
              <>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label="Start">
                    <Select value={rec.when} onChange={(e) => setRec({ ...rec, when: e.target.value as 'asap' | 'at' })}>
                      <option value="asap">as soon as the encoder is running</option>
                      <option value="at">at a time…</option>
                    </Select>
                  </Field>
                  {rec.when === 'at' && (
                    <Field label="Start time" hint={recordingAtInPast ? 'That is in the past: it will start as soon as the encoder is running.' : 'Your computer\'s time zone.'}>
                      <Input type="datetime-local" value={rec.at} onChange={(e) => setRec({ ...rec, at: e.target.value })} />
                    </Field>
                  )}
                  <Field label="Stop after">
                    <Select value={rec.hours} onChange={(e) => setRec({ ...rec, hours: e.target.value })}>
                      <option value="">when the encoder stops</option>
                      {[1, 2, 3, 4, 6, 8, 12].map((h) => <option key={h} value={h}>{h} h</option>)}
                    </Select>
                  </Field>
                </div>
                <div className="grid gap-3 sm:grid-cols-5">
                  <Field label="Resolution">
                    <Select value={rec.resolution || (encodes[0]?.resolution === '1080i' ? '1080p' : encodes[0]?.resolution) || ''} onChange={(e) => setRec({ ...rec, resolution: e.target.value })}>
                      {opts.resolutions.map((r) => <option key={r}>{r}</option>)}
                    </Select>
                  </Field>
                  <Field label="Frame rate">
                    <Select value={rec.fps || encodes[0]?.fps || ''} onChange={(e) => setRec({ ...rec, fps: e.target.value })}>
                      {opts.frameRates.map((r) => <option key={r}>{r}</option>)}
                    </Select>
                  </Field>
                  <Field label="Bitrate (Mbit)">
                    <Input type="number" min={1} max={20} step={0.5} value={rec.mbps} onChange={(e) => setRec({ ...rec, mbps: Number(e.target.value) })} />
                  </Field>
                  <Field label="Audio" hint="Every pair is its own track in the file.">
                    <Select
                      value={rec.pairs.length > 1 ? 'all' : 'one'}
                      onChange={(e) => setRec({ ...rec, pairs: e.target.value === 'all' ? Array.from({ length: audioPairs }, (_, i) => i + 1) : [1] })}
                    >
                      <option value="one">Pair 1 only</option>
                      <option value="all" disabled={audioPairs < 2}>All {audioPairs} pairs</option>
                    </Select>
                  </Field>
                  <Field label="Transcript" hint="Amazon Transcribe, about US$1.44 an hour.">
                    <label className="flex items-center gap-1.5 pt-1.5 text-xs">
                      <input type="checkbox" checked={rec.transcribe} onChange={(e) => setRec({ ...rec, transcribe: e.target.checked })} />
                      Transcribe what is said
                    </label>
                  </Field>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>

      <div className="space-y-4 lg:sticky lg:top-20 lg:self-start">
        <Card title="Cost per hour">
          <div className="p-4">
            {estimate ? <CostBreakdown est={estimate} rtmpLabels={Object.fromEntries(rtmp.map((r, i) => [String(i), r.label]))} /> : (
              <div className="text-xs text-ink-muted">{estimateNote ?? 'Pricing…'}</div>
            )}
            {estimateNote && estimate && <div className="mt-2 text-xs text-ink-muted">{estimateNote}</div>}
          </div>
        </Card>
        <Card title="What this creates">
          <div className="space-y-3 p-4 text-xs">
            <div>
              <div className="text-ink-muted">MediaLive</div>
              <div className="font-mono">{names.channel}</div>
              {names.rtmpChannels.map((n) => <div key={n} className="font-mono">{n}</div>)}
            </div>
            <div>
              <div className="text-ink-muted">Ingest flows</div>
              {names.ingest.map((n) => <div key={n} className="font-mono">{n}</div>)}
            </div>
            <div>
              <div className="text-ink-muted">Output flows</div>
              {names.outputs.map((n) => <div key={n} className="font-mono">{n}</div>)}
            </div>
            {media.length > 0 && (
              <div>
                <div className="text-ink-muted">Clip inputs</div>
                <div>{media.length} MediaLive file input{media.length === 1 ? '' : 's'}</div>
              </div>
            )}
            {rec.on && (
              <div>
                <div className="text-ink-muted">Recorder</div>
                <div className="font-mono">{(name.trim().toUpperCase() || 'NAME')}-REC</div>
                <div className="text-ink-muted">
                  {rec.when === 'at' && rec.at && !recordingAtInPast ? `starts ${new Date(rec.at).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })}` : 'starts when the encoder is running'}
                  {rec.hours ? `, stops after ${rec.hours} h` : ''}
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-grid p-4">
            <Button variant="primary" className="w-full" loading={busy} disabled={name.trim().length < 2 || uploading} onClick={() => void launch()}>
              {uploading ? 'Waiting for uploads…' : 'Launch and start'}
            </Button>
            <p className="mt-2 text-xs text-ink-muted">Everything is created and started - about three minutes.</p>
          </div>
        </Card>
      </div>

      <Modal
        open={!!tplEdit}
        onClose={() => setTplEdit(null)}
        title="Rename template"
        footer={
          <>
            <Button onClick={() => setTplEdit(null)}>Cancel</Button>
            <Button variant="primary" loading={tplBusy} disabled={!tplEdit || tplEdit.name.trim().length < 2} onClick={() => void tplAction(async () => {
              await api.patch(`/encoders/templates/${templateId}`, tplEdit);
              setTplEdit(null);
            })}>Save</Button>
          </>
        }
      >
        {tplEdit && (
          <div className="space-y-3">
            <Field label="Name">
              <Input value={tplEdit.name} maxLength={80} onChange={(e) => setTplEdit({ ...tplEdit, name: e.target.value })} autoFocus />
            </Field>
            <Field label="Description">
              <textarea
                className="w-full rounded-md bg-plane px-3 py-2 text-sm text-ink ring-1 ring-hairline placeholder:text-ink-muted"
                rows={4} maxLength={1000}
                value={tplEdit.description}
                onChange={(e) => setTplEdit({ ...tplEdit, description: e.target.value })}
              />
            </Field>
          </div>
        )}
      </Modal>
    </div>
  );
}
